import {
  Button,
  Typography,
  Box,
  Grid,
  Select,
  MenuItem,
  TextField,
  useTheme,
  styled,
  Container,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Campaign,
  CampaignClient,
  Category,
  EditUserDto,
  PendingGrantsClient,
  PendingGrantsDto,
  TokenDto,
  UsersClient,
} from "../../utilities/backend/client";
import InvestFeedback from "./User/InvestFeedback";
import { useSelector } from "react-redux";
import { RootState } from "../../utilities/redux/store";
import { Close } from "@mui/icons-material";
import { ShareModalMain } from "./User/ShareModal";

const commonLinkStyles = {
  fontWeight: 400,
  textDecoration: "underline",
  color: "#474747",
};

const BottomBlock = styled(Box)(({ theme }) => ({
  borderRadius: "14px",
  backgroundImage: "url(/image_share.png)",
  backgroundSize: "cover",
  padding: "0 60px 140px",
  [theme.breakpoints.down("sm")]: {
    padding: "60px 16px 73px",
  },
}));

const BottomTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Ubuntu",
  fontSize: 36,
  fontWeight: 700,
  color: "#fff",
  marginBottom: "8px",
  textAlign: "center",
  [theme.breakpoints.down("sm")]: {
    fontSize: 26,
  },
}));

const BottomDescription = styled(Typography)(({ theme }) => ({
  fontFamily: "Ubuntu",
  fontSize: 16,
  fontWeight: 600,
  lineHeight: "120%",
  marginBottom: "25px",
  color: "#fff",
  textAlign: "center",
}));

const Content = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  margin: "auto",
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

const dafProviders = [
  { label: "Fidelity Charitable", value: "Fidelity Charitable" },
  { label: "Jewish Foundation", value: "Jewish Foundation" },
  {
    label: "National Philanthropic Trust",
    value: "National Philanthropic Trust",
  },
  { label: "Schwab Charitable", value: "Schwab Charitable" },
  {
    label: "Silicon Valley Community Foundation",
    value: "Silicon Valley Community Foundation",
  },
  { label: "Vanguard Charitable", value: "Vanguard Charitable" },
  { label: "Other", value: "Other" },
];

const dafLinks = {
  "Fidelity Charitable":
    "https://charitablegift.fidelity.com/public/login/donor",
  "Jewish Foundation":
    "https://www.iphiview.com/ujef/Home/tabid/326/Default.aspx",
  "National Philanthropic Trust": "https://nptgivingpoint.org/",
  "Schwab Charitable": "https://www.schwab.com/",
  "Silicon Valley Community Foundation":
    "https://donor.siliconvalleycf.org/s/login/",
  "Vanguard Charitable": "https://www.vanguardcharitable.org/",
};

const InvestmentFormDAF = () => {
  const [dafProvider, setDafProvider] = useState("");
  const [dafName, setDafName] = useState("");
  const [otherDaf, setOtherDaf] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [user, setUser] = useState<EditUserDto>({} as EditUserDto);
  const [allThemes, setAllThemes] = useState<Category[]>();
  const [campain, setCampain] = useState<Campaign>({} as Campaign);

  const token: TokenDto = {
    token: useSelector((state: RootState) => state.token.token) || "",
  };

  const pendingGrantsClient = new PendingGrantsClient();
  const campaignClient = new CampaignClient();
  const userClient = new UsersClient();

  const navigate = useNavigate();
  const theme = useTheme();
  const location = useLocation();
  const { email, firstName, lastName, campaignId, investmentAmount } =
    location.state || {};
  const [sentFeedback, setSentFeedback] = useState<boolean>(false);
  const [showFeedback, setShowFeedback] = useState<boolean>(false);
  const [showSharePage, setShowSharePage] = useState(false);
  const [showAddFundsPage, setShowAddFundsPage] = useState(false);

  const pendingGrant: PendingGrantsDto = {
    userName: firstName,
    lastName: lastName,
    email: email,
    amount: investmentAmount.toString(),
    dAFProvider: dafProvider,
    dAFName: dafName,
    url: dafLinks[dafProvider as keyof typeof dafLinks],
    InvestmentId: campaignId.toString(),
  };

  useEffect(() => {
    if (token.token) {
      userClient
        .users_GetUser(token)
        .then((res) => {
          setUser(res);
        })
        .catch((error) => {
          console.log(error);
        });
    } else {
      setUser({
        email,
        firstName,
        lastName,
        feedback: false,
      });
    }

    campaignClient
      .campaign_GetData()
      .then((data) => {
        const setFilteredThemes = data.theme?.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setAllThemes(setFilteredThemes);
      })
      .catch((error) => {
        const errorObject = JSON.parse(error.response);
        if (errorObject.status === 404) {
          navigate("/find");
        }
      });

    if (campaignId) {
      campaignClient
        .campaign_GetCampaign(campaignId)
        .then((campaign) => {
          setCampain(campaign);
        })
        .catch((error) => {
          const errorObject = JSON.parse(error.response);
          if (errorObject.status === 404) {
            navigate("/find");
          }
        });
    }
  }, []);

  const handleSubmit = () => {
    setShowConfirmation(true);
  };

  const handleDonateDAFClick = () => {
    setIsSubmitting(true);
    pendingGrant.dAFProvider = dafProvider === "Other" ? otherDaf : dafProvider;
    pendingGrantsClient
      .pendingGrant_Create(pendingGrant)
      .then()
      .catch((error) => console.error("Error:", error))
      .finally(() => setIsSubmitting(false));

    if (campaignId) {
      const url = dafLinks[dafProvider as keyof typeof dafLinks];
      if (url) {
        window.open(url, "_blank");
      }
      setShowConfirmation(false);
      setShowSharePage(true);
    } else {
      setShowConfirmation(false);
      setShowAddFundsPage(true);
    }
  };

  const handleDoItLaterClick = () => {
    setIsSubmitting(true);
    pendingGrant.dAFProvider = dafProvider === "Other" ? otherDaf : dafProvider;
      
      pendingGrantsClient
      .pendingGrant_Create(pendingGrant)
      .then(() => {
        if (campaignId) {
          setShowConfirmation(false);
          setShowSharePage(true);
        } else {
          setShowConfirmation(false);
          setShowAddFundsPage(true);
        }
        setIsSubmitting(false);
      })
      .catch((error) => console.error("Error:", error));
  };

  const SharePage = () => {
    const headerFeedback = "Thank you and spread the word!";
    const suHeaderFeedback = (
      <>
        Thank you for the additional details, which CataCap will use only to
        alert you on new Investments matching your{" "}
        <Link
          to={"https://catacap.org/privacy-policy/"}
          rel="noopener noreferrer"
          style={{
            color: "white",
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          preferences
        </Link>
        .
      </>
    );

    return (
      <BottomBlock sx={{ position: "relative" }}>
        <Button
          onClick={() => {
            navigate("/find");
          }}
          sx={{ position: "absolute", top: 30, right: 30 }}
        >
          <Close fontSize="large" sx={{ color: "white" }} />
        </Button>
        <BottomTitle
          sx={{
            fontFamily: "Poppins, Sans-serif",
            paddingTop: 15,
            [theme.breakpoints.down("sm")]: {
              paddingTop: 5,
            },
          }}
        >
          {headerFeedback}
        </BottomTitle>

        <BottomDescription
          sx={{
            fontFamily: "Poppins, Sans-serif",
            fontSize: 16,
            paddingTop: 8,
            [theme.breakpoints.down("sm")]: {
              paddingTop: 3,
            },
          }}
        >
          {suHeaderFeedback}
        </BottomDescription>
        <ShareModalMain campaign={campain} />
      </BottomBlock>
    );
  };

  if (showConfirmation) {
    return (
      <>
        {
          <Grid container justifyContent="center" alignItems="center" pt={10}>
            {isSubmitting ? (
              <Box sx={{ margin: "0 auto" }}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid item xs={12} sm={6} md={6}>
                <Box sx={{ padding: "40px" }}>
                  <Typography
                    sx={{
                      fontSize: 44,
                      fontFamily: "Poppins",
                      lineHeight: 1.23,
                      fontWeight: 600,
                      color: "#20527F",
                      [theme.breakpoints.down("sm")]: {
                        fontSize: 32,
                      },
                    }}
                  >
                    Thank you for your DAF commitment
                  </Typography>
                  <Typography
                    mt={8}
                    sx={{
                      fontSize: "16px",
                      lineHeight: "26px",
                      fontFamily: "Poppins, sans-serif",
                      fontWeight: 400,
                      color: "#000000",
                    }}
                  >
                    Once your DAF is in transit, you’ll receive your login
                    credentials from CataCap. Let’s get your started, as it
                    takes only a few minutes:
                  </Typography>

                  <Box sx={{ padding: "10px" }}>
                    <Typography
                      mt={2}
                      sx={{
                        fontSize: "16px",
                        lineHeight: "26px",
                        fontFamily: "Poppins, sans-serif",
                        fontWeight: 400,
                        color: "#000000",
                      }}
                    >
                      1. You’ll receive an email with all details. We also
                      suggest that you make the request now; it’s easy.
                    </Typography>

                    <Typography
                      mt={2}
                      sx={{
                        fontSize: "16px",
                        lineHeight: "26px",
                        fontFamily: "Poppins, sans-serif",
                        fontWeight: 400,
                        color: "#000000",
                      }}
                    >
                      2. Go to&nbsp;
                      <a
                        href={dafLinks[dafProvider as keyof typeof dafLinks]}
                        style={commonLinkStyles}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {dafProvider}
                      </a>
                      &nbsp;and make a grant to the Impactree Foundation in
                      support of the CataCap project.
                    </Typography>
                    <Typography
                      mt={2}
                      sx={{
                        fontSize: "16px",
                        lineHeight: "26px",
                        fontFamily: "Poppins, sans-serif",
                        fontWeight: 400,
                        color: "#000000",
                      }}
                    >
                      Include the following details:
                      <br />
                      Donation Recipient: Impactree Foundation
                      <br />
                      Project Name: CataCap
                      <br />
                      EIN: 86-2370923
                      <br />
                      Email:&nbsp;
                      <a
                        href="mailto:support@catacap.org"
                        style={commonLinkStyles}
                      >
                        support@catacap.org
                      </a>
                      <br />
                      Address: 3749 Buchanan St unit 475207, San Francisco, CA
                      94147
                    </Typography>
                    <Typography
                      mt={2}
                      sx={{
                        fontFamily: "Poppins, sans-serif",
                        fontWeight: 400,
                      }}
                    >
                      3. Please forward any confirmation email you receive from
                      your DAF provider to&nbsp;
                      <a
                        href="mailto:support@catacap.org"
                        style={commonLinkStyles}
                      >
                        support@catacap.org
                      </a>
                      &nbsp;so that we can immediately apply your investment.
                    </Typography>
                  </Box>
                  <Typography
                    mt={2}
                    sx={{
                      fontFamily: "Poppins, sans-serif",
                      fontWeight: 400,
                    }}
                  >
                    Thank you. Your support is vital to our mission.
                  </Typography>
                  <Box mt={8}>
                    <Grid container spacing={2} justifyContent="center">
                      <Grid item xs={6} display="flex" justifyContent="center">
                        <Button
                          onClick={handleDoItLaterClick}
                          variant="contained"
                          fullWidth
                          sx={{
                            fontFamily: "Poppins, Sans-serif",
                            backgroundColor: "#20527F",
                            color: "#FFF",
                            textTransform: "none",
                          }}
                          disabled={isSubmitting}
                        >
                          Do it Later
                        </Button>
                      </Grid>
                      <Grid item xs={6} display="flex" justifyContent="center">
                        <Button
                          onClick={handleDonateDAFClick}
                          variant="contained"
                          fullWidth
                          sx={{
                            fontFamily: "Poppins, Sans-serif",
                            textTransform: "none",
                            backgroundColor: "#80b73f",
                            "&:hover": { backgroundColor: "#9DCF5F" },
                            color: "#FFF",
                          }}
                          disabled={isSubmitting}
                        >
                          Donate now from Your DAF
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        }
      </>
    );
  }

  if (showSharePage) {
    return (
      <Box pt={8}>
        {!user.feedback ? (
          <InvestFeedback
            themes={allThemes!}
            user={user}
            backClick={() => {
              setShowSharePage(false);
              setShowConfirmation(true);
            }}
            investmentInProcessShow={() => {
              user.feedback = true;
              setSentFeedback(true);
            }}
          />
        ) : (
          <Container
            sx={{
              paddingTop: "70px",
              paddingBottom: "30px",
              [theme.breakpoints.down("sm")]: {
                paddingTop: "20px",
              },
            }}
          >
            <Content>
              <SharePage />
            </Content>
          </Container>
        )}
      </Box>
    );
  }

  if (showAddFundsPage) {
    return (
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        pt={8}
        sx={{ position: "relative" }}
      >
        <Button
          onClick={() => navigate("/find")}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "auto",
            display: "flex",
            alignItems: "center",
            padding: 5,
          }}
        >
          <ArrowBackIcon sx={{ color: "000000" }} />
          <Typography
            sx={{
              fontFamily: "Ubuntu",
              fontSize: 16,
              fontWeight: 400,
              color: "#474747",
              marginLeft: "8px",
            }}
          >
            Back
          </Typography>
        </Button>
        <Grid item xs={12} sm={6} md={6}>
          <Box p={4} display="flex" flexDirection="column">
            <Typography
              sx={{
                fontSize: 44,
                fontFamily: "Poppins",
                lineHeight: 1.23,
                fontWeight: 600,
                color: "#20527F",
                [theme.breakpoints.down("sm")]: {
                  fontSize: 32,
                },
              }}
              gutterBottom
            >
              Thank you for adding funds to your CataCap Account.
            </Typography>
            <Typography
              sx={{
                marginTop: 5,
                fontSize: 16,
                fontFamily: "Poppins",
                lineHeight: 1.23,
                fontWeight: 400,
                [theme.breakpoints.down("sm")]: {
                  fontSize: 16,
                },
              }}
              gutterBottom
            >
              You will receive an email as soon as the funds have arrived from
              your DAF or Foundation and added to your CataCap Account
            </Typography>
            {!user.feedback && (
              <Box
                mt={5}
                pt={5}
                display="flex"
                justifyContent="center"
                width="100%"
              >
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#80b73f",
                    color: "#FFF",
                    padding: "10px 20px",
                    maxWidth: "400px",
                    width: "100%",
                    textTransform: "none",
                    "&:hover": { backgroundColor: "#9DCF5F" },
                  }}
                  onClick={() => {
                    setShowAddFundsPage(false);
                    setShowFeedback(true);
                  }}
                >
                  Take our User Survey
                </Button>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (showFeedback) {
    return (
      <Box pt={2}>
        <Grid item xs={12} sm={6} md={6} pt={8}>
          {showFeedback && (
            <InvestFeedback
              themes={allThemes!}
              user={user}
              backClick={() => {
                setShowFeedback(false);
                setShowConfirmation(true);
              }}
              investmentInProcessShow={() => {
                user.feedback = true;
                navigate("/find");
              }}
            />
          )}
        </Grid>
      </Box>
    );
  }

  return (
    <Grid container justifyContent="center" alignItems="center" pt={5}>
      <Grid item xs={12} sm={6} md={6}>
        <Box p={4} display="flex" flexDirection="column">
          <Typography
            sx={{
              fontSize: 44,
              fontFamily: "Poppins",
              lineHeight: 1.23,
              fontWeight: 600,
              color: "#20527F",
              [theme.breakpoints.down("sm")]: {
                fontSize: 32,
              },
            }}
            gutterBottom
          >
            Donating to Invest with a DAF is easy
          </Typography>

          <Box mt={2} pt={2} sx={{ width: "100%", maxWidth: "400px" }}>
            <Typography
              fontFamily={"Poppins, sans-serif"}
              fontWeight={500}
              gutterBottom
              color="#656565"
            >
              Select Your DAF Provider:
            </Typography>
            <Select
              fullWidth
              value={dafProvider}
              onChange={(e) => setDafProvider(e.target.value)}
              displayEmpty
              sx={{
                "& .MuiSelect-select": { padding: "14px 14px" },
              }}
            >
              {dafProviders.map((provider) => (
                <MenuItem key={provider.value} value={provider.value}>
                  {provider.label}
                </MenuItem>
              ))}
            </Select>

            {dafProvider === "Other" && (
              <Box mt={2} sx={{ width: "100%", maxWidth: "400px" }}>
                <TextField
                  fullWidth
                  placeholder="Please specify"
                  value={otherDaf}
                  onChange={(e) => setOtherDaf(e.target.value)}
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "51px",
                      minHeight: "51px",
                    },
                    "& .MuiInputBase-input": {
                      padding: "14px 14px",
                      lineHeight: "1.5",
                    },
                  }}
                />
              </Box>
            )}
          </Box>

          <Box mt={4} sx={{ width: "100%", maxWidth: "400px" }}>
            <Typography
              fontFamily={"Poppins"}
              fontWeight={500}
              color="#656565"
              gutterBottom
            >
              Please share the name of your DAF:
            </Typography>
            <TextField
              fullWidth
              value={dafName}
              onChange={(e) => setDafName(e.target.value)}
              sx={{
                "& .MuiInputBase-root": { height: "51px", minHeight: "51px" },
                "& .MuiInputBase-input": {
                  padding: "14px 14px",
                  lineHeight: "1.5",
                },
              }}
            />
          </Box>

          <Box
            mt={3}
            pt={5}
            display="flex"
            justifyContent="center"
            width="100%"
          >
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#80b73f",
                color: "#FFF",
                padding: "10px 20px",
                maxWidth: "400px",
                width: "100%",
                textTransform: "none",
                "&:hover": { backgroundColor: "#9DCF5F" },
              }}
              disabled={
                !dafProvider ||
                (dafProvider === "Other" && otherDaf.length === 0)
              }
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default InvestmentFormDAF;
